import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Typography, Stack, Link, Alert } from '@mui/material'
import {
  CommonButton,
  CommonSelect,
  CommonDatePicker,
  CommonTextField,
  CommonDialog,
} from '@/components/common'
import {
  useGetProcedureTypes,
  useGetSponsors,
  useGetPrivateDriveSearch,
} from '@/hooks'
import { useDonorContext } from '@/context'
import { ROUTES, DONOR_PORTAL_SCREEN_CONFIG_CODE } from '@/constants'
import {
  DonorPortalScreenMessage,
  getEligibilityDateHelperText,
} from '@/components/donation'
import {
  getSearchBySponsorParams,
  getSuggestedDonationDateFromProcedureEligibility,
  getEarliestSuggestedDonationDateFromEligibilities,
  parseIsoDateToLocalNoon,
  isValidSponsorAccountId,
  isValidDriveId,
  isDateRangeSearchValid,
  scheduleParamsFromPrivateDriveResponse,
} from '@/utils'

const PRIVATE_EVENT_MODE_PARAM = 'private_event'

export const SearchBySponsor = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { donor, getDonor } = useDonorContext()
  const { procedureTypes, loadingProcedureTypes, getProcedureTypes } =
    useGetProcedureTypes()
  const { sponsors, loadingSponsors, getSponsors } = useGetSponsors()
  const {
    privateDriveError,
    getPrivateDriveSearch,
    loadingPrivateDriveSearch,
    clearPrivateDriveSearch,
  } = useGetPrivateDriveSearch()

  const [privateEventDialogOpen, setPrivateEventDialogOpen] = useState(false)
  const [privateDriveId, setPrivateDriveId] = useState('')
  const [driveIdTouched, setDriveIdTouched] = useState(false)
  const [dialogError, setDialogError] = useState(null)

  const [formData, setFormData] = useState({
    account_id: '',
    start_date: null,
    end_date: null,
    procedure_type_id: '',
  })

  useEffect(() => {
    getProcedureTypes()
    getSponsors()
  }, [])

  useEffect(() => {
    getDonor()
  }, [getDonor])

  useEffect(() => {
    if (searchParams.get('mode') !== PRIVATE_EVENT_MODE_PARAM) return
    setPrivateEventDialogOpen(true)
    const next = new URLSearchParams(searchParams)
    next.delete('mode')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  useEffect(() => {
    const pid = formData.procedure_type_id
    if (!donor?.procedure_eligibilities?.length) return
    const suggested = pid
      ? getSuggestedDonationDateFromProcedureEligibility(
          donor.procedure_eligibilities,
          pid
        )
      : getEarliestSuggestedDonationDateFromEligibilities(
          donor.procedure_eligibilities
        )
    if (!suggested) return
    setFormData(s =>
      s.start_date != null
        ? s
        : { ...s, start_date: suggested, end_date: s.end_date }
    )
  }, [donor?.procedure_eligibilities, formData.procedure_type_id])

  const sponsorOptions = useMemo(
    () =>
      (sponsors || []).map(s => ({
        id: String(s.id),
        name: s.name,
        earliest_drive_date: s.earliest_drive_date ?? null,
      })),
    [sponsors]
  )

  const driveIdError = useMemo(() => {
    if (!driveIdTouched) return null
    if (!privateDriveId.trim()) return 'Private event number is required.'
    if (!isValidDriveId(privateDriveId))
      return 'Enter a valid numeric private event number.'
    return null
  }, [driveIdTouched, privateDriveId])

  const continueDisabled =
    !isValidDriveId(privateDriveId) || loadingPrivateDriveSearch

  const displayDialogError = dialogError || privateDriveError

  const resetPrivateEventDialog = () => {
    setPrivateDriveId('')
    setDriveIdTouched(false)
    setDialogError(null)
    clearPrivateDriveSearch()
  }

  const handleOpenPrivateEventDialog = () => {
    resetPrivateEventDialog()
    setPrivateEventDialogOpen(true)
  }

  const handleClosePrivateEventDialog = () => {
    setPrivateEventDialogOpen(false)
    resetPrivateEventDialog()
  }

  const handleSponsorSearch = () => {
    if (!isValidSponsorAccountId(formData.account_id)) return
    const params = getSearchBySponsorParams(formData)
    navigate(`${ROUTES.DONATE_SPONSOR_RESULTS}?${params.toString()}`)
  }

  const handlePrivateEventContinue = async () => {
    setDriveIdTouched(true)
    setDialogError(null)
    clearPrivateDriveSearch()
    if (!isValidDriveId(privateDriveId)) return

    const result = await getPrivateDriveSearch({ drive_id: privateDriveId })
    if (!result.ok || !result.data) return

    const scheduleParams = scheduleParamsFromPrivateDriveResponse(result.data)
    if (
      !scheduleParams.get('operation_id') ||
      !scheduleParams.get('operation_type') ||
      !scheduleParams.get('date')
    ) {
      setDialogError('Unable to open scheduling for this drive.')
      return
    }

    handleClosePrivateEventDialog()
    navigate(`${ROUTES.SCHEDULE_DONATION}?${scheduleParams.toString()}`)
  }

  const isDisabled = useMemo(() => {
    if (!isValidSponsorAccountId(formData.account_id)) return true
    if (!isDateRangeSearchValid(formData.start_date, formData.end_date))
      return true
    return false
  }, [formData])

  return (
    <>
      <Stack spacing={2} sx={{ pb: 1 }}>
        <DonorPortalScreenMessage
          code={DONOR_PORTAL_SCREEN_CONFIG_CODE.SEARCH_BY_SPONSOR_SEARCH}
        />
        <Box>
          <Typography variant='subtitle1' sx={{ mb: 0.5 }}>
            Which sponsor are you looking for?
          </Typography>
          <CommonSelect
            id='sbs-sponsor'
            label='Sponsor'
            placeholder='Select a sponsor'
            enableSort={false}
            options={sponsorOptions}
            value={formData.account_id}
            loading={loadingSponsors}
            onChange={v => {
              const account_id = v || ''
              const sponsor = sponsorOptions.find(s => s.id === account_id)
              const earliest = sponsor?.earliest_drive_date
                ? parseIsoDateToLocalNoon(String(sponsor.earliest_drive_date))
                : null
              setFormData(s => ({
                ...s,
                account_id,
                start_date: earliest ?? s.start_date,
              }))
            }}
            required
          />
          <Box sx={{ py: 1, textAlign: 'left' }}>
            <Link
              component='button'
              type='button'
              variant='body2'
              underline='hover'
              onClick={handleOpenPrivateEventDialog}
              sx={{
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                font: 'inherit',
                p: 0,
                textAlign: 'left',
              }}
            >
              Have a private event number?
            </Link>
          </Box>
        </Box>

        <Box>
          <Typography variant='subtitle1' sx={{ mb: 0.5 }}>
            When do you want to donate?
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems='flex-start'
          >
            <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
              <CommonDatePicker
                id='sbs-start-date'
                label='Start date'
                value={formData.start_date}
                onChange={d => setFormData(s => ({ ...s, start_date: d }))}
                disablePast
                required
                helperText={getEligibilityDateHelperText({
                  donorId: donor?.id,
                  procedureEligibilities: donor?.procedure_eligibilities,
                  procedureTypeId: formData.procedure_type_id,
                  startDate: formData.start_date,
                })}
              />
            </Box>
            <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
              <CommonDatePicker
                id='sbs-end-date'
                label='End date'
                value={formData.end_date}
                onChange={d => setFormData(s => ({ ...s, end_date: d }))}
                disablePast
                helperText='Leave blank to show all future dates'
              />
            </Box>
          </Stack>
        </Box>

        <Box>
          <Typography variant='subtitle1' sx={{ mb: 0.5 }}>
            What do you want to donate?
          </Typography>
          <CommonSelect
            id='sbs-procedures'
            label='Procedures'
            placeholder='All Available'
            enableSort={false}
            options={procedureTypes}
            value={formData.procedure_type_id}
            loading={loadingProcedureTypes}
            onChange={v => {
              const procedure_type_id = v || ''
              setFormData(s => {
                const next = { ...s, procedure_type_id }
                if (!procedure_type_id) return next
                const suggested =
                  getSuggestedDonationDateFromProcedureEligibility(
                    donor?.procedure_eligibilities,
                    procedure_type_id
                  )
                if (suggested && !next.start_date) {
                  next.start_date = suggested
                }
                return next
              })
            }}
          />
        </Box>

        <Box pt={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <CommonButton
            id='sbs-cta'
            label='Search'
            variant='primary'
            color='primary'
            size='large'
            disabled={isDisabled}
            onClick={handleSponsorSearch}
          />
        </Box>
      </Stack>

      <CommonDialog
        open={privateEventDialogOpen}
        onClose={handleClosePrivateEventDialog}
        title='Enter private event number'
        maxWidth='sm'
        fullWidth
        persistent={false}
        actions={
          <CommonButton
            id='sbs-private-continue'
            label='Continue'
            variant='primary'
            color='primary'
            size='large'
            disabled={continueDisabled}
            loading={loadingPrivateDriveSearch}
            onClick={handlePrivateEventContinue}
          />
        }
      >
        <Stack spacing={2}>
          {displayDialogError && (
            <Alert
              severity='error'
              onClose={() => {
                setDialogError(null)
                clearPrivateDriveSearch()
              }}
            >
              {displayDialogError}
            </Alert>
          )}
          <CommonTextField
            id='sbs-drive-id'
            label='Private event number'
            placeholder='e.g. 12345'
            value={privateDriveId}
            restrictedValues={/^\d*$/}
            onChange={v => {
              setPrivateDriveId(v)
              setDialogError(null)
              clearPrivateDriveSearch()
            }}
            onBlur={() => setDriveIdTouched(true)}
            error={!!driveIdError}
            helperText={driveIdError}
            required
          />
        </Stack>
      </CommonDialog>
    </>
  )
}

export default SearchBySponsor
